/* eslint-disable import/no-named-as-default */
import React, { useRef, useContext } from "react";
import PropTypes from "prop-types";
import { Context } from "~context/MainProvider.js";
import Search from "~organisms/search";
import Link from "~hooks/link";
import Menu from "~molecules/menu";
import MarketDropdown from "~atoms/market-dropdown";
import Notifications from "~atoms/notifications";
import { toggleScroll } from "~helpers/scroll";
import ConditionalWrapper from "~atoms/conditional-wrapper";
import Admin from "~atoms/admin/bar";
import Sticky from "~molecules/sticky";
import CustomComponent from "~atoms/menu/variables";
import { pageTypes } from "../../../constants/site-settings/navigation";
import NavigationProvider from "./navigationContext";

import styles from "./navigation.module.scss";

const Navigation = ({
  section,
  pageContext,
  sticky = true,
  orientation = "horizontal",
  mobileAnimation = "collapseFull",
  stopScrollOnOpen = true,
  canOpenAllSubMenus = true,
  logo = "/images/logo.svg",
  logoWidth = 200,
  logoHeight = 31,
  searchIcon = null,
  template,
  children,
  customStyles,
  hasCustomComponent = false,
  disableMarketDropdown = false,
  hasSearch = true,
  disableSearch,
  megaMenu = false,
  menu,
}) => {
  
  const navRef = useRef(React.createRef());
  const { admin } = useContext(Context) || {};
  const showMenu =
    pageTypes[template]?.showNavMenu ?? pageTypes.default.showNavMenu;

  const showNotifications =
    pageTypes[template]?.showNotification ?? pageTypes.default.showNotification;

  let searchURLPageName = "s";

  const showLogoWithLink =
    pageTypes[template]?.showLogoWithLink ?? pageTypes.default.showLogoWithLink;

  const logoImg = (
    <img
      alt={`${process.env.GATSBY_SITE_NAME} logo`}
      src={logo}
      width={logoWidth}
      height={logoHeight}
    />
  );

  if (stopScrollOnOpen) toggleScroll("", true);

  const activeMarket =
    pageContext?.allMarkets && pageContext?.allMarkets[pageContext.page.market];
  if (activeMarket) activeMarket.market = pageContext?.page?.market;

  if (activeMarket?.path_prefix) {
    searchURLPageName = `${activeMarket?.path_prefix}/s`;
  }

  const onClickHandler = () => {
    if (
      typeof window !== "undefined" &&
      window.location.href.replace(window.location.origin, "") === "/"
    ) {
      window.location.reload();
    }
  };

  return (
    <ConditionalWrapper
      condition={sticky}
      wrapper={(items) => (
        <Sticky
          position="top"
          fullWidth
          showOnScroll={false}
          className={`${styles.stickyNav || ""} ${
            admin ? styles.adminBar : ""
          }`}
        >
          {items}
        </Sticky>
      )}
    >
      {admin && (
        <Admin
          page={pageContext.page}
          marketSections={pageContext.marketSections}
          menu={menu}
          activeMarket={activeMarket}
        />
      )}
      <nav className={styles.nav || ""} ref={navRef}>
        <NavigationProvider>
          {showMenu && showLogoWithLink ? (
            <Link
              className={`${styles.logo || ""} main-menu-gtm logo-cta`}
              aria-label="Nav Logo"
              to={activeMarket?.path_prefix || "/"}
              onClick={onClickHandler}
            >
              {logoImg}
            </Link>
          ) : (
            <div className={`${styles.logo || ""}`}>{logoImg}</div>
          )}
          {showMenu && (
            <>
              <Menu
                pageContext={pageContext}
                section={section}
                orientation={orientation}
                mobileAnimation={mobileAnimation}
                stopScrollOnOpen={stopScrollOnOpen}
                canOpenAllSubMenus={canOpenAllSubMenus}
                customStyles={customStyles}
                gtmClass="main-menu-gtm"
                menu={menu}
                enableMegaMenu={megaMenu}
              />

              <div className={styles.navIconContainer}>
                {activeMarket && !disableMarketDropdown && <MarketDropdown pageContext={pageContext} />}
                {showNotifications && <Notifications section={section} />}
                {hasCustomComponent && (
                  <CustomComponent
                    pageContext={pageContext}
                    section={section}
                  />
                )}
                {hasSearch && (
                  <Search
                    className={styles.search || ""}
                    searchIcon={searchIcon}
                    pageContext={pageContext}
                    searchURLPageName={searchURLPageName}
                    marketPrefix={activeMarket?.path_prefix || "/"}
                    isDisabled={disableSearch}
                  />
                )}
              </div>
            </>
          )}
        </NavigationProvider>
      </nav>
      {children}
    </ConditionalWrapper>
  );
};

Navigation.propTypes = {
  section: PropTypes.shape({
    modules: PropTypes.arrayOf(
      PropTypes.shape({
        short_code: PropTypes.string,
        children: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            value: PropTypes.string,
          })
        ),
      })
    ),
  }).isRequired,
  pageContext: PropTypes.shape({
    marketSections: PropTypes.shape({}),
    allMarkets: PropTypes.shape({}),
    page: PropTypes.shape({
      market: PropTypes.string,
    }),
  }),
  hasSearch: PropTypes.bool,
  sticky: PropTypes.bool,
  orientation: PropTypes.oneOf(["horizontal"]),
  mobileAnimation: PropTypes.oneOf([
    "collapseFull",
    "collapse",
    "slideLeft",
    "slideTop",
  ]),
  stopScrollOnOpen: PropTypes.bool,
  disableMarketDropdown: PropTypes.bool,
  canOpenAllSubMenus: PropTypes.bool,
  template: PropTypes.string,
  searchIcon: PropTypes.string,
  children: PropTypes.node,
  customStyles: PropTypes.shape({}),
  hasCustomComponent: PropTypes.bool,
  disableSearch: PropTypes.bool,
  megaMenu: PropTypes.bool,
  menu: PropTypes.string,
  logo: PropTypes.string,
  logoWidth: PropTypes.number,
  logoHeight: PropTypes.number,
};

export default Navigation;
